<template>
  <hb-basic-page
    :action-buttons="$vuetify.breakpoint.smAndDown ? {create: titleButton} : {}"
    :fill-height="loading || tasks.length === 0"
    :loading="loading"
    :search-title="$t('tasks_management.search_from_tasks')"
    :subtle-loading="subtleLoading"
    :title="$t('tasks_management.title')"
    :title-button="titleButton"
    name="tasks-management-view"
    @handle-search="handleSearch"
  >
    <v-col v-if="!loading && tasks.length > 0" cols="12" offset-xl="1"
           xl="10">
      <v-data-table
        :footer-props="{ itemsPerPageOptions: [50, 100, 500, -1] }"
        :headers="headers"
        :items="filteredTasks"
        :items-per-page="50"
        :loading="loading"
        :options.sync="options"
        style="cursor: pointer"
        @click:row="openEditDialog"
      >
        <template v-slot:item.nameText="{item}">
          <div class="py-3">
            <strong>{{ item.nameText }}</strong>
            <div>{{ item.descriptionText }}</div>
          </div>
        </template>

        <template v-slot:item.priority="{item}">
          <div v-if="item.priority">
            <v-icon :class="[taskPriorityColor(item) + '--text']" class="mr-1 mt-n1">
              {{ taskPriorityIcon(item) }}
            </v-icon>
            {{ $t(`tasks.priorities.${item.priority}`) }}
          </div>
        </template>

        <template v-slot:item.swotType="{item}">
          <v-chip v-if="item.swotType" :color="swotTypeColor(item)" :class="swotTextColor(item)" x-small>
            {{ $t('swot_types.' + item.swotType) }}
          </v-chip>
        </template>

        <template v-slot:item.actions="{item}">
          <v-btn color="error" icon @click.stop.prevent="deleteTask(item)">
            <v-icon>mdi mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-col>

    <v-col v-if="!loading && tasks.length === 0" class="pa-0 fill-height" cols="12" lg="10" md="10" offset-lg="1"
           offset-md="1" offset-xl="2"
           xl="8">
      <v-row align="center" class="fill-height" no-gutters>
        <no-content-view :description="$t('tasks_management.no_tasks_help')"
                         :function-button="true"
                         :function-button-text="$t('tasks_management.create_task')"
                         :prop-function="handleCreateTask"
                         :reload-button="true"
                         :title="$t('tasks_management.no_tasks')"
                         function-button-icon="add"
                         title-icon="mdi mdi-playlist-edit"/>
      </v-row>
    </v-col>

    <edit-task-settings-dialog ref="editTaskDialog" :categories="richCategories" @refresh="reloadTasks"/>

  </hb-basic-page>
</template>

<script>
import NoContentView from "@/components/NoContentView";
import EditTaskSettingsDialog from "@/components/TaskManagement/EditTaskSettingsDialog";
import staticTaskApi from "@/api/statictask";
import TranslationService from "@/service/translation.service";
import debounce from "lodash.debounce";
import categoryService from "@/service/category.service";

export default {
  name: "TasksManagementView",
  components: {EditTaskSettingsDialog, NoContentView},
  data() {
    return {
      loading: true,
      subtleLoading: false,
      options: {},
      tasks: [],
      titleButton: {
        text: this.$t('tasks_management.create_task'),
        icon: 'add',
        callback: this.handleCreateTask,
        primary: true,
      },
      headers: [
        {
          text: this.$t('tasks_management.task_title_and_description'),
          value: 'nameText',
        },
        {
          text: this.$tc('tasks_management.task_type', 1),
          value: 'type',
        },
        {
          text: this.$tc('categories.title', 1),
          value: 'categoryText',
        },
        {
          text: this.$t('tasks_management.task_priority'),
          value: 'priority',
          width: '150px',
        },
        {
          text: this.$t('tasks_management.task_swot_type'),
          value: 'swotType',
          width: '150px',
        },
        {
          value: 'actions',
          width: '60px',
          sortable: false,
        },
      ],
      searchString: '',
    }
  },
  methods: {
    handleCreateTask() {
      this.$refs.editTaskDialog.open();
    },
    openEditDialog(task) {
      this.$refs.editTaskDialog.open(task.id);
    },
    async reloadTasks() {
      if (this.tasks.length > 0) {
        this.subtleLoading = true;
        this.loading = false;
      } else {
        this.subtleLoading = false;
        this.loading = true;
      }
      try {
        let tasks = await staticTaskApi.getStaticTasks();
        tasks = tasks.map(task => {
          task.nameText = TranslationService.t(task.nameTid);
          task.descriptionText = TranslationService.t(task.descriptionTid);

          task.categoryText = '';
          const categoryIndex = this.richCategories.findIndex(c => c.id === task.categoryId);
          if (categoryIndex > -1) {
            task.categoryText = this.richCategories[categoryIndex].nameTextLabel;
          }

          task.searchString = `${task.nameText} ${task.descriptionText} ${task.categoryText}`;

          return task;
        });
        this.tasks = tasks;
      } catch (e) {
        this.$handleApiError(e, this.$t('errors.error_loading_tasks'));
      }
      this.loading = false;
      this.subtleLoading = false;
    },
    handleSearch(e) {
      this.searchString = e;
    },
    getCategoriesRecursively(categories, level = 0) {
      return categories.map(c => {
        c.level = level;
        c.children = this.getCategoriesRecursively(this.categories.filter(cc => cc.parentCategory?.id === c.id), level + 1);
        return c;
      });
    },
    taskPriorityColor(task) {
      if (!task) return null;
      return task.priority === 'HIGH' ? 'error' : task.priority === 'MEDIUM' ? 'warning' : 'info';
    },
    taskPriorityIcon(task) {
      if (!task) return null;
      return task.priority === 'HIGH' ? 'mdi mdi-chevron-up' : task.priority === 'MEDIUM' ? 'mdi mdi-drag-horizontal-variant' : 'mdi mdi-chevron-down';
    },
    swotTypeColor(task) {
      return this.$colorByType(task.swotType);
    },
    swotTextColor(task) {
      return this.$textColorAgainstBGByType(task.swotType);
    },
    async deleteTask(task) {
      const confirm = await this.$confirmDelete(this.$t('tasks_management.confirm_delete'), this.$t('tasks_management.confirm_delete_text'));
      if (!confirm) return;

      try {
        await staticTaskApi.deleteStaticTask(task.id);
        this.$showSuccessNotification(this.$t('tasks_management.task_deleted_successfully'));
        this.reloadTasks();
      } catch (error) {
        this.$handleApiError(error, this.$t('errors.error_deleting_task'));
      }
    },
  },
  computed: {
    filteredTasks() {
      return this.tasks.filter(task => {
        return task.searchString.toLowerCase().includes(this.searchString.toLowerCase());
      });
    },
    categories() {
      const categories = [...categoryService.richAllCategories];
      return categories;
    },
    richCategories() {
      let categories = [...categoryService.richAllCategories];
      categories = categories.flatMap(category => [category, ...(category.level === 1 ? category?.children : [])]);
      categories.forEach(c => {
        c.nameTextLabel = [c.nameText, c?.parentCategory?.nameText, c?.parentCategory?.parentCategory?.nameText]
          .reverse()
          .filter(Boolean)
          .join(' > ');
      });
      return categories;
    },
  },
  created() {
    this.debouncedSearch = debounce(this.handleSearch, 450, {maxWait: 5000});
  },
  async mounted() {
    if (!this.$hasMatchingRoles(['admin'])) {
      this.$router.replace({name: 'access_denied'});
      return;
    }
    await this.reloadTasks();
    this.loading = false;
  }
}
</script>

<style scoped>

</style>
